import { useEffect, useState } from "react";
import { Check, Layers, Loader2, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../../connect/api";
import { Avatar } from "../ui";

export const presetSummary = (p) => {
  const o = [];
  if (p.access.channel !== "both") o.push(p.access.channel === "web" ? "Web only" : "Mobile only");
  if (p.access.office_ip_only) o.push("Office IP");
  if (p.perms.dept_visibility === "all") o.push("All dept groups");
  if (p.perms.location_filter) o.push(p.perms.location_filter.replace("NST | ", ""));
  if (p.perms.directory === "department") o.push("Dept directory");
  if (p.perms.group_visibility === "own_dept") o.push("Dept group only");
  if (p.perms.inter_dept) o.push("Inter-dept");
  if (!p.perms.broadcasts) o.push("No broadcasts");
  if (!p.perms.direct_messages) o.push("Reply only");
  if (!p.perms.attachments) o.push("No files");
  if (p.perms.cross_office) o.push("Sees other office");
  return o.length ? o.join(" · ") : "Fully open";
};

/** Pick real staff and apply one preset to all of them in one tap. */
const ApplySheet = ({ preset, onClose, onApplied }) => {
  const [staff, setStaff] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/staff", { params: { real: 1, limit: 500 } }).then((r) => setStaff(r.data.items.filter((s) => !s.is_ceo))).catch(() => setStaff([])); }, []);
  const shown = (staff || []).filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.department.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/perm-presets/${preset.id}/apply`, { staff_ids: [...sel] });
      toast.success(`"${preset.name}" applied to ${data.matched} staff`); onApplied();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0e1626] border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col nxc-fade-in" data-testid="preset-apply-sheet">
        <div className="px-4 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
          <Users size={18} className="text-amber-300" />
          <div className="flex-1 min-w-0"><h2 className="nxc-display text-base font-semibold truncate">Apply "{preset.name}"</h2><div className="text-[11px] text-slate-400">{presetSummary(preset)}</div></div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="preset-apply-close"><X size={18} /></button>
        </div>
        <div className="px-4 py-2 flex items-center gap-2 border-b border-white/5">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or department" className="nxc-input flex-1 h-9 text-sm" data-testid="preset-apply-search" />
          <button type="button" onClick={() => setSel(new Set(shown.map((s) => s.id)))} className="text-[11px] text-amber-300 font-semibold shrink-0" data-testid="preset-apply-select-all">Select all</button>
          <button type="button" onClick={() => setSel(new Set())} className="text-[11px] text-slate-400 shrink-0" data-testid="preset-apply-clear">Clear</button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {staff === null ? <Loader2 size={16} className="animate-spin text-amber-300 mx-auto my-6" /> : shown.map((s) => (
            <button type="button" key={s.id} onClick={() => toggle(s.id)} className={`w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left border ${sel.has(s.id) ? "border-amber-400/50 bg-amber-500/10" : "border-transparent hover:bg-white/5"}`} data-testid={`preset-apply-staff-${s.id}`}>
              <Avatar name={s.name} src={s.avatar} size={34} />
              <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-100 truncate">{s.name}</div><div className="text-[11px] text-slate-400 truncate">{s.department} · {(s.office || "").replace("NST | ", "")}</div></div>
              <span className={`h-5 w-5 rounded-full border flex items-center justify-center ${sel.has(s.id) ? "border-amber-300 bg-amber-400 text-black" : "border-white/20"}`}>{sel.has(s.id) && <Check size={12} />}</span>
            </button>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
          <span className="text-xs text-slate-400">{sel.size} selected</span><span className="flex-1" />
          <button type="button" disabled={busy || !sel.size} onClick={apply} className="nxc-btn nxc-btn-gold h-10 !px-5" data-testid="preset-apply-confirm">{busy ? <Loader2 size={15} className="animate-spin" /> : <Layers size={15} />} Apply to {sel.size || ""}</button>
        </div>
      </div>
    </div>
  );
};

/** List of saved presets: apply to many staff, or delete. */
export const PermPresets = ({ onClose, onChanged }) => {
  const [items, setItems] = useState(null);
  const [applying, setApplying] = useState(null);
  const load = () => api.get("/admin/perm-presets").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"? Staff keep their current permissions.`)) return;
    try { await api.delete(`/admin/perm-presets/${p.id}`); toast.success("Preset deleted"); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0e1626] border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto nxc-fade-in" data-testid="perm-presets-sheet">
        <div className="sticky top-0 bg-[#0e1626] px-4 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
          <Layers size={18} className="text-amber-300" />
          <div className="flex-1"><h2 className="nxc-display text-base font-semibold">Permission presets</h2><div className="text-[11px] text-slate-400">Save a set from any staff member's Permissions sheet, then apply it here.</div></div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="perm-presets-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-2">
          {items === null ? <Loader2 size={16} className="animate-spin text-amber-300 mx-auto my-6" /> : items.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No presets yet. Open a staff member's Permissions and tap "Save as preset".</p> : items.map((p) => (
            <div key={p.id} className="nxc-card p-3 flex items-center gap-3" data-testid={`perm-preset-${p.id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-100 truncate">{p.name}</div><div className="text-[11px] text-slate-400 truncate">{presetSummary(p)}</div><div className="text-[10px] text-slate-500 mt-0.5">{p.staff_count} staff on this preset</div></div>
              <button type="button" onClick={() => setApplying(p)} className="nxc-btn nxc-btn-gold h-8 text-xs !px-3" data-testid={`perm-preset-apply-${p.id}`}><Users size={12} /> Apply</button>
              <button type="button" onClick={() => del(p)} className="p-1.5 rounded-full text-rose-300 hover:bg-rose-500/10" aria-label="Delete preset" data-testid={`perm-preset-delete-${p.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        {applying && <ApplySheet preset={applying} onClose={() => setApplying(null)} onApplied={() => { setApplying(null); load(); onChanged?.(); }} />}
      </div>
    </div>
  );
};
